import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import Editor from '@monaco-editor/react';
import { useQuery } from '@tanstack/react-query';
import { useState, useRef, useMemo, useCallback, useEffect, useContext } from 'react';
import { Button, Spinner, Table, useLocalizeMessage, useToastNotification, SegmentedControl, Select, } from 'libs.nucleus';
import { ApiClientService, AuthContext, useApiClient } from 'libs.react_internal';
import { StudiesContext } from '../../../contexts';
import { resolveCubeApiUrl } from '../../../data_reports/constants/cube.constants';
import { isLegacyInfrastructure } from '../../constants/data_view.constants';
import { toSnakeCase } from '../../utils';
const CONTINUE_WAIT_MAX_ATTEMPTS = 60;
const CONTINUE_WAIT_INTERVAL_MS = 1000;
const postCubeLoadWithContinueWait = async (baseUrl, body, headers) => {
    for (let attempt = 0; attempt < CONTINUE_WAIT_MAX_ATTEMPTS; attempt++) {
        const response = await fetch(`${baseUrl}/load`, {
            method: 'POST',
            credentials: 'include',
            headers,
            body: JSON.stringify(body),
        });
        const payload = await response.json().catch(() => ({}));
        if (response.ok && typeof payload?.error === 'string' && payload.error.includes('Continue wait')) {
            await new Promise((resolve) => setTimeout(resolve, CONTINUE_WAIT_INTERVAL_MS));
            continue;
        }
        return { ok: response.ok && !payload?.error, payload };
    }
    return { ok: false, payload: { error: 'Timed out waiting for query result' } };
};
export const AddEditMeasureStep2 = ({ formData, onFormChange, dataView, isEditMode = false, isReadOnly = false, onQueryValidationChange, }) => {
    const translate = useLocalizeMessage();
    const toast = useToastNotification();
    const { entityId } = useContext(AuthContext);
    const { studies } = useContext(StudiesContext);
    const webClient = useApiClient(ApiClientService.WEB);
    const momClient = useApiClient(ApiClientService.MOM);
    const usesNewInfrastructure = !isLegacyInfrastructure(dataView.data?.data_source);
    const editorRef = useRef(null);
    const monacoRef = useRef(null);
    const previewContainerRef = useRef(null);
    const [useSqlEditor, setUseSqlEditor] = useState(formData.calculationType === 'SQL');
    const [sqlQuery, setSqlQuery] = useState(formData.sql || '');
    const [queryResult, setQueryResult] = useState([]);
    const [isRunningQuery, setIsRunningQuery] = useState(false);
    const [isValidatingMeasure, setIsValidatingMeasure] = useState(false);
    const [measureFunction, setMeasureFunction] = useState(formData.measureFunction || 'Count');
    const [countDimension, setCountDimension] = useState(formData.dimension || '');
    const [hasRunQuery, setHasRunQuery] = useState(false);
    const [dataType, setDataType] = useState(formData.type || 'number');
    // Segmented control items
    const segmentedControlItems = useMemo(() => [
        { label: translate('Form'), id: 'form' },
        { label: translate('Expression'), id: 'expression' },
    ], [translate]);
    const handleSegmentChange = useCallback((_item, index) => {
        const isExpression = index === 1;
        setUseSqlEditor(isExpression);
    }, []);
    // Notify parent about query validation state
    useEffect(() => {
        const isValid = hasRunQuery && queryResult.length > 0;
        onQueryValidationChange?.(isValid);
    }, [hasRunQuery, queryResult.length, onQueryValidationChange]);
    // Local state for study and environment selection (for preview only)
    const [selectedStudy, setSelectedStudy] = useState(undefined);
    const [selectedEnvironment, setSelectedEnvironment] = useState(undefined);
    const studyOptions = useMemo(() => studies.map((study) => ({
        label: study.name,
        value: study.id.toString(),
    })), [studies]);
    const fetchEnvironments = useCallback(async () => {
        if (!selectedStudy?.value) {
            return [];
        }
        try {
            const { data: response } = await momClient.get(`/v1/entities/${entityId}/studies/${selectedStudy.value}/environments`, {
                params: {
                    clusterTypes: 'dev',
                    withCortexAccess: true,
                },
            });
            return response.data.filter((env) => !env.deployment.isProd);
        }
        catch (error) {
            return [];
        }
    }, [selectedStudy?.value, entityId, momClient]);
    const { data: environments = [], isLoading: isLoadingEnvironments } = useQuery({
        queryKey: ['entities', entityId, 'studies', selectedStudy?.value, 'environments'],
        queryFn: fetchEnvironments,
        enabled: !!entityId && !!selectedStudy?.value,
        refetchOnWindowFocus: false,
    });
    const environmentOptions = useMemo(() => environments.map((env) => ({
        label: env.name,
        value: env.cortexId || env.id.toString(),
    })), [environments]);
    // Reset environment when study changes
    const handleStudyChange = useCallback((option) => {
        setSelectedStudy(option);
        setSelectedEnvironment(undefined);
    }, []);
    const handleEnvironmentChange = useCallback((option) => {
        setSelectedEnvironment(option);
    }, []);
    // Update state when formData changes (e.g., when editing)
    useEffect(() => {
        setUseSqlEditor(formData.calculationType === 'SQL');
        setSqlQuery(formData.sql || '');
        setMeasureFunction(formData.measureFunction || 'Count');
        setCountDimension(formData.dimension || '');
        setDataType(formData.type || 'number');
        // Reset query result when switching between edit/create mode
        if (isEditMode) {
            setQueryResult([]);
        }
    }, [formData.calculationType, formData.sql, formData.measureFunction, formData.dimension, formData.type, isEditMode]);
    // Get available dimensions for the dropdown
    const dimensionOptions = useMemo(() => {
        if (!dataView.data?.dimensions) {
            return [];
        }
        return Object.entries(dataView.data.dimensions).map(([key, dimension]) => ({
            label: dimension.title,
            value: key,
        }));
    }, [dataView.data?.dimensions]);
    // Data type options
    const dataTypeOptions = useMemo(() => [
        { label: translate('Number'), value: 'number' },
        { label: translate('Text'), value: 'string' },
        { label: translate('Date Time'), value: 'time' },
    ], [translate]);
    const handleDataTypeChange = (option) => {
        const value = option?.value || 'number';
        setDataType(value);
        onFormChange({
            ...formData,
            type: value,
        });
    };
    // Measure function options
    const measureFunctionOptions = useMemo(() => [
        { label: translate('Count'), value: 'Count' },
        { label: translate('Sum'), value: 'Sum' },
        { label: translate('Average'), value: 'Average' },
        { label: translate('Min'), value: 'Min' },
        { label: translate('Max'), value: 'Max' },
    ], [translate]);
    useEffect(() => {
        const calculationType = useSqlEditor ? 'SQL' : 'DROPDOWN';
        onFormChange({
            ...formData,
            calculationType,
            sql: sqlQuery,
            measureFunction,
            dimension: countDimension,
        });
    }, [useSqlEditor, sqlQuery, measureFunction, countDimension, formData, onFormChange]);
    const handleEditorDidMount = (editor, monaco) => {
        editorRef.current = editor;
        monacoRef.current = monaco;
    };
    const handleSQLChange = useCallback((value) => {
        const newSql = value || '';
        setSqlQuery(newSql);
        onFormChange({
            ...formData,
            sql: newSql,
        });
    }, [formData, onFormChange]);
    const handleRunQuery = useCallback(async () => {
        if (!sqlQuery.trim()) {
            toast.addNotification({
                type: 'error',
                title: translate('Please enter a SQL query'),
            });
            return;
        }
        if (!selectedStudy?.value || !selectedEnvironment?.value) {
            toast.addNotification({
                type: 'error',
                title: translate('Please select a study and environment to run queries'),
            });
            return;
        }
        const cubeName = dataView.data?.name;
        if (!cubeName) {
            toast.addNotification({
                type: 'error',
                title: translate('Dataset is missing cube name'),
                subtitle: translate('This dataset cannot be validated because its cube name is not set.'),
            });
            return;
        }
        setIsRunningQuery(true);
        setQueryResult([]);
        try {
            const measureName = (formData.name && toSnakeCase(formData.name)) || 'custom_measure';
            const { ok, payload } = await postCubeLoadWithContinueWait(resolveCubeApiUrl({ env: 'test' }), {
                query: {
                    measures: [`${cubeName}.${measureName}`],
                    limit: 100,
                    renewQuery: true,
                },
                validateMeasure: {
                    cubeName,
                    measureName,
                    definition: {
                        sql: sqlQuery.trim(),
                        type: dataType,
                        title: formData.name || measureName,
                        ...(formData.description ? { description: formData.description } : {}),
                    },
                },
            }, {
                'Content-Type': 'application/json',
                'Cache-Control': 'must-revalidate',
                'X-Entity-Id': String(entityId || ''),
                'X-Study-Id': selectedStudy.value,
                'X-Env': 'test',
                'X-Orgs': JSON.stringify([selectedEnvironment.value]),
                'X-Preaggs': 'true',
                'X-Cube-Config-Ids': JSON.stringify([dataView.id]),
            });
            if (!ok) {
                toast.addNotification({
                    type: 'error',
                    title: translate('Measure is invalid'),
                    subtitle: payload?.error || translate('Failed to validate measure'),
                });
                setQueryResult([]);
            }
            else {
                const prefix = `${cubeName}.`;
                const rows = (payload.data || []).map((row) => Object.fromEntries(Object.entries(row).map(([k, v]) => [k.startsWith(prefix) ? k.slice(prefix.length) : k, v])));
                setQueryResult(rows);
            }
            setHasRunQuery(true);
        }
        catch (error) {
            const message = error instanceof Error ? error.message : translate('Failed to validate measure');
            toast.addNotification({
                type: 'error',
                title: translate('Error validating measure'),
                subtitle: message,
            });
            setQueryResult([]);
            setHasRunQuery(true);
        }
        finally {
            setIsRunningQuery(false);
        }
    }, [
        sqlQuery,
        selectedStudy?.value,
        selectedEnvironment?.value,
        entityId,
        dataView.id,
        dataView.data?.name,
        formData.name,
        formData.description,
        dataType,
        toast,
        translate,
    ]);
    const handleValidateMeasure = useCallback(async () => {
        if (!sqlQuery.trim()) {
            toast.addNotification({
                type: 'error',
                title: translate('Please enter a SQL query'),
            });
            return;
        }
        if (!selectedStudy?.value || !selectedEnvironment?.value) {
            toast.addNotification({
                type: 'error',
                title: translate('Please select a study and environment to validate measures'),
            });
            return;
        }
        const cubeName = dataView.data?.name;
        if (!cubeName) {
            toast.addNotification({
                type: 'error',
                title: translate('Dataset is missing cube name'),
                subtitle: translate('This dataset cannot be validated because its cube name is not set.'),
            });
            return;
        }
        setIsValidatingMeasure(true);
        try {
            const measureName = (formData.name && toSnakeCase(formData.name)) || 'custom_measure';
            const { ok, payload } = await postCubeLoadWithContinueWait(resolveCubeApiUrl({ env: 'test' }), {
                query: {
                    measures: [`${cubeName}.${measureName}`],
                    limit: 1,
                    renewQuery: true,
                },
                validateMeasure: {
                    cubeName,
                    measureName,
                    definition: {
                        sql: sqlQuery.trim(),
                        type: dataType,
                        title: formData.name || measureName,
                        ...(formData.description ? { description: formData.description } : {}),
                    },
                },
            }, {
                'Content-Type': 'application/json',
                'Cache-Control': 'must-revalidate',
                'X-Entity-Id': String(entityId || ''),
                'X-Study-Id': selectedStudy.value,
                'X-Env': 'test',
                'X-Orgs': JSON.stringify([selectedEnvironment.value]),
                'X-Preaggs': 'true',
                'X-Cube-Config-Ids': JSON.stringify([dataView.id]),
            });
            if (!ok) {
                toast.addNotification({
                    type: 'error',
                    title: translate('Measure is invalid'),
                    subtitle: payload?.error || translate('Failed to validate measure'),
                });
            }
            else {
                toast.addNotification({
                    type: 'success',
                    title: translate('Measure is valid'),
                });
            }
        }
        catch (error) {
            const message = error instanceof Error ? error.message : translate('Failed to validate measure');
            toast.addNotification({
                type: 'error',
                title: translate('Error validating measure'),
                subtitle: message,
            });
        }
        finally {
            setIsValidatingMeasure(false);
        }
    }, [
        sqlQuery,
        selectedStudy?.value,
        selectedEnvironment?.value,
        entityId,
        dataView.id,
        dataView.data?.name,
        formData.name,
        formData.description,
        dataType,
        toast,
        translate,
    ]);
    useEffect(() => {
        const container = previewContainerRef.current;
        if (!container) {
            return;
        }
        const tableRoot = container.querySelector('[data-testid="nucleus-table--container"]');
        if (!tableRoot) {
            return;
        }
        tableRoot.style.height = '100%';
        tableRoot.style.minHeight = '0';
        const tableWrapper = tableRoot.querySelector(':scope > .nucleus-relative');
        if (tableWrapper) {
            tableWrapper.style.flex = '1';
            tableWrapper.style.minHeight = '0';
            tableWrapper.style.display = 'flex';
            tableWrapper.style.flexDirection = 'column';
        }
        const scrollWrapper = container.querySelector('.nucleus-overflow-x-auto');
        if (scrollWrapper) {
            scrollWrapper.style.overflow = 'auto';
            scrollWrapper.style.minHeight = '0';
        }
    }, [queryResult, isRunningQuery]);
    const columns = useMemo(() => {
        return queryResult.length > 0
            ? Object.keys(queryResult[0]).map((column) => ({
                header: column,
                accessorKey: column,
            }))
            : [];
    }, [queryResult]);
    const handleMeasureFunctionChange = (option) => {
        const value = option?.value || '';
        setMeasureFunction(value);
        onFormChange({
            ...formData,
            measureFunction: value,
        });
    };
    const handleDimensionChange = (option) => {
        const value = option?.value || '';
        setCountDimension(value);
        onFormChange({
            ...formData,
            dimension: value,
        });
    };
    const handleRunDropdownQuery = useCallback(async () => {
        if (!measureFunction || !countDimension) {
            toast.addNotification({
                type: 'error',
                title: translate('Please select measure function and dimension'),
            });
            return;
        }
        if (!selectedStudy?.value || !selectedEnvironment?.value) {
            toast.addNotification({
                type: 'error',
                title: translate('Please select a study and environment to run queries'),
            });
            return;
        }
        setIsRunningQuery(true);
        setQueryResult([]);
        try {
            // Build SQL query based on measure function and dimension
            const dimensionSql = dataView.data?.dimensions?.[countDimension]?.sql || countDimension;
            const measureName = `${measureFunction.toLowerCase()}_${countDimension}`;
            const baseSql = dataView.data?.sql?.trim();
            if (!baseSql) {
                toast.addNotification({
                    type: 'error',
                    title: translate('Dataset SQL query is missing'),
                    subtitle: translate('The dataset SQL query is required to preview measures. Please ensure dimensions were created with a SQL query.'),
                });
                setIsRunningQuery(false);
                return;
            }
            // Generate SQL based on measure function
            // Wrap the base SQL query in parentheses to use it as a subquery
            let sql = '';
            switch (measureFunction) {
                case 'Count':
                    sql = `SELECT COUNT(DISTINCT ${dimensionSql}) as "${measureName}" FROM (${baseSql})`;
                    break;
                case 'Sum':
                    sql = `SELECT SUM(${dimensionSql}) as "${measureName}" FROM (${baseSql})`;
                    break;
                case 'Average':
                    sql = `SELECT AVG(${dimensionSql}) as "${measureName}" FROM (${baseSql})`;
                    break;
                case 'Min':
                    sql = `SELECT MIN(${dimensionSql}) as "${measureName}" FROM (${baseSql})`;
                    break;
                case 'Max':
                    sql = `SELECT MAX(${dimensionSql}) as "${measureName}" FROM (${baseSql})`;
                    break;
                default:
                    sql = `SELECT COUNT(DISTINCT ${dimensionSql}) as "${measureName}" FROM (${baseSql})`;
            }
            const response = await webClient.post(`/api/data_transfer/configs/query`, {
                sql: `${sql} LIMIT 100`,
                explain: false,
                environmentId: selectedEnvironment.value,
                studyId: selectedStudy.value,
                useAuthGateway: usesNewInfrastructure,
            });
            setQueryResult(response.data || []);
            setHasRunQuery(true);
        }
        catch (error) {
            const axiosError = error;
            toast.addNotification({
                type: 'error',
                title: translate('Error running query'),
                subtitle: axiosError.response?.data?.message || translate('Failed to execute SQL query'),
            });
            setQueryResult([]);
            setHasRunQuery(true);
        }
        finally {
            setIsRunningQuery(false);
        }
    }, [
        measureFunction,
        countDimension,
        selectedStudy?.value,
        selectedEnvironment?.value,
        webClient,
        toast,
        translate,
        dataView.data,
    ]);
    return (_jsxs("div", { className: 'grid grid-cols-2 gap-4 size-full min-h-[500px] overflow-hidden', children: [_jsxs("div", { className: 'space-y-4 size-full flex flex-col min-w-0', children: [_jsxs("div", { className: 'flex items-center justify-between pb-4 border-b', children: [_jsx("div", { children: translate('Editor') }), _jsxs("div", { className: 'flex items-center gap-4', children: [_jsx(Select, { placeholder: translate('Select a study'), options: studyOptions, value: selectedStudy, onChange: handleStudyChange, width: 'full', searchableOptions: true }), _jsx(Select, { placeholder: translate('Select an environment'), options: environmentOptions, value: selectedEnvironment, onChange: handleEnvironmentChange, width: 'full', disabled: !selectedStudy?.value || isLoadingEnvironments, searchableOptions: true }), useSqlEditor && (_jsx(Button, { label: translate('Validate'), onClick: handleValidateMeasure, disabled: isValidatingMeasure || isRunningQuery || !sqlQuery.trim(), variant: 'secondary' })), _jsx(Button, { label: translate('Run'), onClick: useSqlEditor ? handleRunQuery : handleRunDropdownQuery, disabled: isRunningQuery ||
                                            isValidatingMeasure ||
                                            (useSqlEditor ? !sqlQuery.trim() : !measureFunction || !countDimension), variant: 'primary' })] })] }), _jsx("div", { className: isReadOnly ? 'pointer-events-none opacity-60' : undefined, children: _jsx(SegmentedControl, { items: segmentedControlItems, onItemClick: handleSegmentChange, initialItemIndex: useSqlEditor ? 1 : 0 }) }), useSqlEditor ? (_jsxs(_Fragment, { children: [_jsx(Select, { label: translate('As data type'), options: dataTypeOptions, value: dataTypeOptions.find((opt) => opt.value === dataType), onChange: handleDataTypeChange, width: 'full', disabled: isReadOnly }), _jsx("div", { className: 'border rounded-lg flex-1 py-4', children: _jsx(Editor, { height: '100%', defaultLanguage: 'sql', value: sqlQuery, onChange: isReadOnly ? undefined : handleSQLChange, onMount: handleEditorDidMount, options: {
                                        minimap: { enabled: false },
                                        fontSize: 14,
                                        formatOnType: true,
                                        lineNumbers: 'on',
                                        automaticLayout: true,
                                        readOnly: isReadOnly,
                                        domReadOnly: isReadOnly,
                                    } }) })] })) : (_jsxs("div", { className: 'flex flex-col gap-6', children: [_jsx(Select, { label: translate('Measure function'), options: measureFunctionOptions, value: measureFunction ? measureFunctionOptions.find((opt) => opt.value === measureFunction) : undefined, onChange: handleMeasureFunctionChange, width: 'full', disabled: isReadOnly }), _jsx("div", { className: 'pl-6', children: _jsx(Select, { label: translate('{measureFunction} dimension', {
                                        measureFunction: measureFunctionOptions.find((opt) => opt.value === measureFunction)?.label || measureFunction,
                                    }), options: dimensionOptions, value: countDimension ? dimensionOptions.find((opt) => opt.value === countDimension) : undefined, onChange: handleDimensionChange, width: 'full', disabled: isReadOnly, searchableOptions: true }) }), _jsx(Select, { label: translate('As data type'), options: dataTypeOptions, value: dataTypeOptions.find((opt) => opt.value === dataType), onChange: handleDataTypeChange, width: 'full', disabled: isReadOnly })] }))] }), _jsxs("div", { className: 'border-l px-4 space-y-4 min-w-0 overflow-hidden flex flex-col', children: [_jsx("div", { className: 'text-md font-semibold', children: translate('Data preview') }), _jsx("div", { ref: previewContainerRef, className: 'flex-1 min-h-0 overflow-hidden', children: isRunningQuery ? (_jsx("div", { className: 'flex items-center justify-center h-full', children: _jsx(Spinner, {}) })) : queryResult.length > 0 ? (_jsx(Table, { columns: columns, data: queryResult, hasPagination: true, paginationNavigationScreenReaderLabel: translate('Data preview pagination'), initialPageSize: 5 })) : (_jsx("div", { className: 'flex items-center justify-center h-full', children: _jsx("div", { className: 'text-center text-text-secondary', children: hasRunQuery
                                    ? translate('No results found.')
                                    : useSqlEditor
                                        ? translate('Enter a measure expression (e.g., SUM(amount), COUNT(DISTINCT id)), then click Run to preview.')
                                        : translate('Select measure function and dimension, then click Run to preview data.') }) })) })] })] }));
};
